import { z } from "zod";

export const FILTER_CONDITIONS = [
  "contains",
  "does_not_contain",
  "is_empty",
  "is_not_empty",
  "starts_with",
  "ends_with",
  "is_exactly",
  "matches_regex",
  "greater_than",
  "less_than",
  "greater_than_or_equal",
  "less_than_or_equal",
  "between",
] as const;

export type FilterCondition = (typeof FILTER_CONDITIONS)[number];

const filterConditionSchema = z.enum(FILTER_CONDITIONS);

const stageCombinatorValues = ["or", "and"] as const;

export type StageCombinator = (typeof stageCombinatorValues)[number];

const stageCombinatorSchema = z.enum(stageCombinatorValues);

const filterStageSchema = z.object({
  value: z.string(),
  condition: filterConditionSchema,
  value2: z.string().optional(),
});

export type FilterStage = z.infer<typeof filterStageSchema>;

// columnFilters is keyed by the logical column id (ColumnDefinition.columnId),
// a collision-free encoding of the message path. Legacy configs keyed by
// message-path id are remapped at load by migratePersistedColumnFilterKeys;
// buildLiveColumnFilters then normalizes the plain-string shape to multi-stage.
// Two value shapes:
//   - plain string:   "tool"
//   - multi-stage:    { stages: [...], combinator? }
const columnFilterValueSchema = z.union([
  z.string(),
  z.object({
    stages: z.array(filterStageSchema),
    combinator: stageCombinatorSchema.optional(),
  }),
]);

export const logPanelConfigSchema = z.object({
  searchFilter: z
    .object({
      term: z.string(),
      mode: z.enum(["string", "regex"] as const),
    })
    .optional(),
  columnFilters: z.record(z.string(), columnFilterValueSchema).optional(),
  filterDisplayMode: z.enum(["hide", "dim"]).optional(),
  // Pixel widths keyed by column id. Time column uses TIME_COLUMN_ID; dynamic
  // columns use ColumnDefinition::columnId. Missing entries fall back to the
  // default layout (12.5rem time, 1fr dynamic).
  columnWidths: z.record(z.string(), z.number().positive()).optional(),
  // Whether the filter bar is open. The bar is not visible by default
  searchVisible: z.boolean().optional(),
});

export type LogPanelConfig = z.infer<typeof logPanelConfigSchema>;
